/**
 * Site Group Settings V2 — "Sharing" section: auto-share this group
 * with internal virtual groups or external third parties via
 * `ThirdPartiesTab` in site-group mode.
 *
 * Contains:
 * - SharingSection (default export)
 */

import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import { useFormikContext } from 'formik';
import React from 'react';
import { useTranslation } from 'react-i18next';

import StatusChip from '@sophocles/components/StatusChip';
import { SettingsSection } from '@sophocles/views/settings/components';
import ThirdPartiesTab from '@sophocles/views/sites/components/StructureCard/ThirdPartiesTab';

// Sharing section.  Drops the existing ThirdPartiesTab in
// site-group mode (no `siteId`); pending share additions /
// removals are folded into the Formik `__pending_shares` synthetic
// field so the unified Save bar activates and the consolidated
// save-flow can dispatch `createAutoShares` / `removeAutoShares`
// alongside the section PATCH.  Same idiom site-settings uses for
// its Access section.
//
// Visibility predicate is verbatim: `is_staff` on the legacy
// `getSections`.  Backend already gates the section presence; we
// render based on `settings.sharing` being present.
//
// `siteGroup` is the plain `{id, description}` pair the parent view
// already holds — ThirdPartiesTab's site-group-sharing path (no
// `siteId`) needs it to resolve the parent group without a selector.
export default function SharingSection({ customerId, siteGroup }) {
  const { t } = useTranslation();
  const { setFieldValue } = useFormikContext();
  const handleChange = React.useCallback(
    changes => {
      setFieldValue(
        '__pending_shares',
        {
          newlyShared: changes?.newlyShared ?? [],
          newlyUnshared: changes?.newlyUnshared ?? [],
        },
        false,
      );
    },
    [setFieldValue],
  );
  const staffChip = <StatusChip color="info" label={t('siteSettings.staffOnly', 'Staff Only')} />;
  return (
    <SettingsSection
      id="settings-sharing"
      icon={<VerifiedUserIcon />}
      title={t('siteGroupSettings.v2.sharing', 'Sharing')}
      description={t(
        'siteGroupSettings.v2.sharingDescription',
        'Auto-share this group with internal virtual groups or external third parties.',
      )}
      chip={staffChip}
    >
      <ThirdPartiesTab customerId={customerId} siteGroup={siteGroup} onChange={handleChange} />
    </SettingsSection>
  );
}
